import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { BugReport, Refresh, Home, ExpandMore } from "@mui/icons-material";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * React Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      // console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // In production, you would send this to an error reporting service
    // Example: Sentry, LogRocket, etc.
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box sx={{ textAlign: "center" }}>
            <BugReport sx={{ fontSize: 64, color: "statusText.attention", mb: 2 }} />

            <Typography variant="h4" fontWeight={600} gutterBottom>
              Something went wrong
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
            >
              A JavaScript error occurred while rendering this page. This has
              been reported to our development team.
            </Typography>

            <Alert severity="error" sx={{ maxWidth: 500, mx: "auto", mb: 3 }}>
              <Typography variant="body2">
                <strong>Error:</strong>{" "}
                {this.state.error?.message || "Unknown error occurred"}
              </Typography>
            </Alert>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                mb: 4,
              }}
            >
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={this.handleGoHome}
              >
                Go Home
              </Button>
              <Button variant="text" onClick={this.handleReset}>
                Try Again
              </Button>
            </Box>

            {/* Development Error Details */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <Accordion sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" fontWeight={600}>
                    Error Details (Development)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                      <strong>Error:</strong> {this.state.error.name}
                      <br />
                      <strong>Message:</strong> {this.state.error.message}
                    </Typography>

                    {this.state.error.stack && (
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          Stack Trace:
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: "12px",
                            backgroundColor: "surfaces.surfaceRaised",
                            p: 2,
                            borderRadius: 1,
                            overflow: "auto",
                            maxHeight: 300,
                          }}
                        >
                          {this.state.error.stack}
                        </Box>
                      </Box>
                    )}

                    {this.state.errorInfo?.componentStack && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          Component Stack:
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: "12px",
                            backgroundColor: "surfaces.surfaceRaised",
                            p: 2,
                            borderRadius: 1,
                            overflow: "auto",
                            maxHeight: 200,
                          }}
                        >
                          {this.state.errorInfo.componentStack}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
